import React, {useCallback, useMemo, useState} from 'react';
import {Autocomplete, Chip, TextField} from '@mui/material';
import TagBadge from '../../../components/documentContent/TagBadge';
import {suggestRequirementTags} from '../../../firebase/firestore/icr';
import {tagColor} from '../tags/tagColor';

function mergeTagOptions(extraOptions, names, selectedTags) {
    return [...new Set([
        ...(Array.isArray(extraOptions) ? extraOptions : []),
        ...(Array.isArray(names) ? names : []),
        ...(Array.isArray(selectedTags) ? selectedTags : []),
    ])].sort((a, b) => a.localeCompare(b));
}

function tagName(option) {
    if (!option) return '';
    if (typeof option === 'string') return option;
    if (option.__create) return option.value || '';
    return option.name || '';
}

function withCreateOption(options, inputValue, selectedTags) {
    const list = [...(Array.isArray(options) ? options : [])];
    const input = (inputValue || '').trim();
    if (!input) {
        return list;
    }
    const lower = input.toLowerCase();
    const selected = new Set((Array.isArray(selectedTags) ? selectedTags : [])
        .map((t) => String(t || '').trim().toLowerCase())
        .filter(Boolean));
    const exists = selected.has(lower) || list.some((opt) => {
        if (!opt || opt.__create) return false;
        return tagName(opt).toLowerCase() === lower;
    });
    if (!exists) {
        list.push({__create: true, label: `Create "${input}"`, value: input});
    }
    return list;
}

function normalizeSelected(newValue) {
    const next = [];
    const seen = new Set();
    for (const item of Array.isArray(newValue) ? newValue : []) {
        const name = tagName(item).trim();
        if (!name) continue;
        const key = name.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        next.push(name);
    }
    return next;
}

/** Multiselect tags field with autosuggest (same UX as /icr search filters). */
export default function RequirementTagsInput({
    value = [],
    onChange,
    size = 'medium',
    fullWidth = true,
    label = 'Tags',
    placeholder = 'Add tag…',
    extraOptions = [],
    suggestFn = suggestRequirementTags,
    suggestLimit = 20,
}) {
    const [options, setOptions] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const tags = Array.isArray(value) ? value : [];

    const handleInput = useCallback(async (nextInput) => {
        try {
            const names = await suggestFn(nextInput || '', suggestLimit);
            setOptions(mergeTagOptions(extraOptions, names, tags));
        } catch {
            setOptions(mergeTagOptions(extraOptions, [], tags));
        }
    }, [extraOptions, suggestFn, suggestLimit, tags]);

    const computedOptions = useMemo(
        () => withCreateOption(options, inputValue, tags),
        [options, inputValue, tags],
    );

    return (
        <Autocomplete
            multiple
            freeSolo
            size={size}
            fullWidth={fullWidth}
            options={computedOptions}
            value={tags}
            inputValue={inputValue}
            filterSelectedOptions
            filterOptions={(x) => x}
            getOptionLabel={(option) => {
                if (!option) return '';
                if (typeof option === 'string') return option;
                if (option.__create) return option.label;
                return option.name || '';
            }}
            getOptionKey={(option) => (option && option.__create
                ? `create:${option.value}`
                : tagName(option))}
            isOptionEqualToValue={(option, val) =>
                tagName(option).toLowerCase() === String(val || '').trim().toLowerCase()}
            onChange={(_, newValue) => {
                onChange(normalizeSelected(newValue));
                setInputValue('');
            }}
            onOpen={() => { handleInput(''); }}
            onInputChange={(_, next, reason) => {
                if (reason === 'reset') {
                    setInputValue(next);
                    return;
                }
                setInputValue(next);
                handleInput(next);
            }}
            renderOption={(props, option) => {
                const {key, ...optionProps} = props;
                if (option && option.__create) {
                    return (
                        <li key={key} {...optionProps}>
                            {option.label}
                        </li>
                    );
                }
                return (
                    <li key={key} {...optionProps}>
                        <TagBadge tag={tagName(option)}/>
                    </li>
                );
            }}
            renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => {
                    const name = tagName(option);
                    const colors = tagColor(name);
                    const {key, ...tagProps} = getTagProps({index});
                    return (
                        <Chip
                            {...tagProps}
                            key={key}
                            size="small"
                            label={name}
                            sx={{
                                fontWeight: 700,
                                bgcolor: colors.bg,
                                color: colors.fg,
                                border: `1px solid ${colors.border}`,
                                '& .MuiChip-deleteIcon': {color: colors.fg},
                            }}
                        />
                    );
                })
            }
            renderInput={(params) => (
                <TextField {...params} label={label} placeholder={placeholder}/>
            )}
        />
    );
}

export {mergeTagOptions, withCreateOption, normalizeSelected};
